import {
  Banner,
  Button,
  ButtonText,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { Layout } from 'src/components/Layout';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from 'src/utils/validation';
import { useAcceptTransfer } from 'src/api/commands';
import { formatDeadlineDate } from 'src/utils/date';
import Title from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Title';
import {
  CustomFormFieldDefinition,
  FormFieldAbstract,
  FormFieldAcceleratedPublication,
  FormFieldAdditionalSupplementary,
  FormFieldAITools,
  FormFieldAnonymizedManuscript,
  FormFieldARRIVEFiles,
  FormFieldCheckbox,
  FormFieldCheckboxArray,
  FormFieldClinicalTrials,
  FormFieldConflictOfInterestForAuthor,
  FormFieldCONSORTFiles,
  FormFieldCorrespondingAuthor,
  FormFieldCoverLetter,
  FormFieldDataAvailability,
  FormFieldDateInput,
  FormFieldDefinition,
  FormFieldDoubleAnonymousPeerReview,
  FormFieldEthicalApproval,
  FormFieldEthicsStatement,
  FormFieldGraphicalAbstractImage,
  FormFieldFiguresAndTables,
  FormFieldFiguresInput,
  FormFieldFileUploadArray,
  FormFieldFocusSelect,
  FormFieldFunding,
  FormFieldFundingGeneric,
  FormFieldInfoText,
  FormFieldIntegerInput,
  FormFieldIPEMMembership,
  FormFieldKeywords,
  FormFieldNoveltyStatement,
  FormFieldOpposedReviewers,
  FormFieldPreferenceImmediatePublication,
  FormFieldPreprint,
  FormFieldPreviouslySubmitted,
  FormFieldPublishingModel,
  FormFieldRadio,
  FormFieldRecommendedReviewers,
  FormFieldSelect,
  FormFieldGeneratedDataset,
  FormFieldUtilizedDataset,
  FormFieldSharedDataset,
  FormFieldStandardReportingGuidelines,
  FormFieldStandardReportingGuidelinesDetails,
  FormFieldStandardReportingGuidelinesFileDetails,
  FormFieldSubtitle,
  FormFieldSupplementary,
  FormFieldTablesInput,
  FormFieldTextInput,
  FormFieldTitlePage,
  FormFieldTitle,
  FormFieldTransparentPeerReview,
  FormFieldCommentForEditor
} from '@publishing-engine/domain/types/article';
import Abstract from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Abstract';
import Keywords from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Keywords';
import SupplementaryUpload from 'src/pages/Organization/public/Journal/SubmissionUpload/components/UploadSupplement';
import DataAvailability from '../../DataAvailability';
import NoveltyStatement from 'src/pages/Organization/public/Journal/SubmissionUpload/components/NoveltyStatement';
import CoverLetter from 'src/pages/Organization/public/Journal/SubmissionUpload/components/CoverLetter';
import FiguresAndTables from 'src/pages/Organization/public/Journal/SubmissionUpload/components/FiguresAndTables';
import DoubleAnonymousReview from 'src/pages/Organization/public/Journal/SubmissionUpload/components/DoubleAnonymousReview';
import ReviewerRecommendation from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ReviewerRecommendation';
import TransparentPeerReview from 'src/pages/Organization/public/Journal/SubmissionUpload/components/TransparentPeerReview';
import EthicsStatement from 'src/pages/Organization/public/Journal/SubmissionUpload/components/EthicsStatement';
import ClinicalTrials from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ClinicalTrials';
import Funding from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Funding';
import PublishingModel from 'src/pages/Organization/public/Journal/SubmissionUpload/components/PublishingModel';
import RadioGroup from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/RadioGroup';
import TextInput from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/TextInput';
import ImmediatePublication from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ImmediatePublication';
import AcceleratedPublication from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AcceleratedPublication';
import SubjectArea from 'src/pages/Organization/public/Journal/SubmissionUpload/components/SubjectArea';
import _ from 'lodash';
import { IUser } from '@publishing-engine/domain/models';
import { Author } from './Author';
import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionContext } from 'src/api/queries';
import { useFormDraft } from 'src/api/mutations';
import { fetchFormDraft } from 'src/api/queries';
import Loading from 'src/pages/Status/Loading';
import { useNavigate } from 'react-router-dom';
import FocusIssue from 'src/pages/Organization/public/Journal/SubmissionUpload/components/FocusIssue';
import useFocusIssueConfig from 'src/hooks/useFocusIssue';
import LicensesAndPermissionForm from 'src/pages/Organization/public/Journal/SubmissionUpload/components/LicensesAndPermissions';
import AuthorSubmissionFormDataProvider from 'src/contexts/SubmissionContextProvider';
import CoAuthorConsent from 'src/pages/Organization/public/Journal/SubmissionUpload/components/CoAuthorConsent';
import { IAuthorStepConfig } from '../../../../hooks/useSubmissionStepsConfig';
import {
  CustomFormFieldComponent,
  FormFieldInteger,
  FormFieldSingleFileUpload
} from 'src/components/FormField';
import GeneralClassifications from 'src/pages/Organization/public/Journal/SubmissionUpload/components/GeneralClassifications';
import StandardReportingGuidelines from 'src/pages/Organization/public/Journal/SubmissionUpload/StandardReportingGuidelines';
import AdditionalSupplementaryUpload from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AdditionalSupplementaryUpload';
import GenericRichTextInput from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/GenericRichTextInput';
import { SourceFileUpload } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/SourceFileUpload';
import Figures from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Figures';
import GraphicalAbstractImageUpload from 'src/pages/Organization/public/Journal/SubmissionUpload/components/GraphicalAbstractImage';
import Tables from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Tables';
import FundingGeneric from 'src/pages/Organization/public/Journal/SubmissionUpload/components/FundingGeneric';
import RadioGroupWithTextArea from 'src/pages/Organization/public/Journal/SubmissionUpload/components/RadioGroupWithTextArea';
import GeneratedDataset from 'src/pages/Organization/public/Journal/SubmissionUpload/components/GeneratedDataset';
import UtilizedDataset from 'src/pages/Organization/public/Journal/SubmissionUpload/components/UtilizedDataset';
import AITools from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AITools';
import { FieldLabel } from 'src/components/Field';
import OpenAccessOnly from 'src/pages/Organization/public/Journal/SubmissionUpload/components/OpenAccessOnly';
import AuthorDeclaration from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AuthorDeclaration';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import CorrespondingAuthor from 'src/pages/Organization/public/Journal/SubmissionUpload/components/CorrespondingAuthor';
import DataAvailabilityStatement from 'src/pages/Organization/public/Journal/SubmissionUpload/components/DataAvailabilityStatement';

export const MissingSubmissionData: React.FC<{ onClose: () => void }> = ({
  onClose
}) => {
  const {
    workflowResponse: {
      forms: {
        authorSubmission: { id: formSchemaId }
      },
      articleWorkflow: { id, articleTypeCategoryId },
      workflowData: { currentDeadline, currentState }
    },
    submissionId,
    submissionContextId,
    fieldsResponse,
    organizationId
  } = useSubmissionData();

  const { guidelineLink, ...focusIssueConfig } =
    useFocusIssueConfig(submissionContextId);

  const { data: formDraft, isLoading: isLoadingFormDraft } = useQuery({
    queryKey: [
      'form-draft',
      submissionId,
      'submission',
      'authorSubmission',
      currentState
    ],
    queryFn: () =>
      fetchFormDraft(
        submissionId,
        'submission',
        'authorSubmission',
        currentState
      ),
    enabled: !!submissionId
  });

  const { data: journal, isLoading: isLoadingJournal } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () => fetchSubmissionContext(submissionContextId),
    enabled: !!submissionContextId
  });

  const { Authors, SubmittingAuthor } = fieldsResponse.fields;

  const authorList = [
    ...((Authors?.value as IUser[]) ?? []),
    ...(SubmittingAuthor?.value ? [SubmittingAuthor?.value] : [])
  ] as IUser[];

  const { configs, defaultValues } = Object.values(
    fieldsResponse?.fields ?? {}
  ).reduce(
    (acc, { formFieldDefinition, externalDependency }) => {
      acc.configs[formFieldDefinition.name] = formFieldDefinition;

      if (formFieldDefinition.name === 'FocusIssue') {
        acc.configs[formFieldDefinition.name] = {
          ...formFieldDefinition,
          params: {
            labels: {
              ...formFieldDefinition.params.labels,
              options: focusIssueConfig.labelOptions
            },
            validations: {
              ...formFieldDefinition.params.validations,
              options: focusIssueConfig.validationOptions
            }
          }
        } as FormFieldSelect;
      }

      const value = _.get(
        formDraft?.data,
        formFieldDefinition.caseDataFields.main
      );
      acc.defaultValues = _.set(
        acc.defaultValues,
        formFieldDefinition.caseDataFields.main,
        value
      );

      if (
        externalDependency &&
        !_.get(acc.defaultValues, externalDependency.path)
      ) {
        acc.defaultValues = _.set(
          acc.defaultValues,
          externalDependency.path,
          externalDependency.pathValue
        );
      }

      return acc;
    },
    {
      configs: {},
      defaultValues: {}
    } as {
      configs: Record<string, FormFieldDefinition>;
      defaultValues: Record<string, unknown>;
    }
  );

  if (isLoadingFormDraft || isLoadingJournal) return <Loading />;

  return (
    <AuthorSubmissionFormDataProvider>
      <MissingSubmissionDataForm
        defaultValues={defaultValues}
        authorList={authorList}
        hasMissingFields={true}
        submissionId={submissionId}
        formSchemaId={formSchemaId}
        articleWorkflowId={id}
        currentDeadline={currentDeadline}
        currentState={currentState}
        organizationId={organizationId}
        submissionContextId={submissionContextId}
        configs={configs}
        journalName={journal?.data.name ?? ''}
        guidelineLink={guidelineLink}
        articleTypeCategoryId={articleTypeCategoryId}
        onClose={onClose}
      />
    </AuthorSubmissionFormDataProvider>
  );
};

const MissingSubmissionDataForm: React.FC<{
  hasMissingFields: boolean;
  submissionContextId: string;
  organizationId: string;
  submissionId: string;
  articleWorkflowId: string;
  formSchemaId: string;
  currentState: string;
  currentDeadline?: Date;
  configs: Record<string, FormFieldDefinition>;
  defaultValues: Record<string, unknown>;
  authorList: IUser[];
  journalName: string;
  guidelineLink?: string;
  articleTypeCategoryId: string;
  onClose: () => void;
}> = ({
  hasMissingFields,
  submissionId,
  organizationId,
  submissionContextId,
  configs,
  defaultValues,
  formSchemaId,
  currentState,
  currentDeadline,
  articleWorkflowId,
  authorList,
  journalName,
  guidelineLink,
  articleTypeCategoryId,
  onClose
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { submissionContext } = useSubmissionData();
  const reviewSubmissionInformation =
    submissionContext?.config.cms.authorSubmissionForm
      .reviewSubmissionInformation;

  const submissionPreferences =
    submissionContext?.config.cms.authorSubmissionForm.submissionPreferences;

  const formDraftMutation = useFormDraft();
  const mutation = useAcceptTransfer(submissionId, organizationId);

  const schema = generateValidationSchema(configs);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, getValues, watch, setValue } = methods;

  const onSaveDraft = async () => {
    const values = getValues();
    const valuesToSave = Object.values(configs ?? {}).reduce((acc, value) => {
      const curr = _.get(values, value.caseDataFields.main) ?? null;

      if (curr !== null && curr !== '') {
        return _.set(acc, value.caseDataFields.main, curr);
      }

      return acc;
    }, {});

    const res = await formDraftMutation.mutateAsync({
      formSchemaId,
      entityId: submissionId,
      entityType: 'submission',
      formType: 'authorSubmission',
      phase: currentState,
      data: valuesToSave
    });

    if (!res.isSuccess)
      return toast({
        variant: 'error',
        title: 'Error Saving draft',
        description:
          res.message?.errors
            ?.map((err: { reason: string }) => err.reason)
            .join(', ') || 'Try again later'
      });

    toast({
      variant: 'success',
      title: 'Saved to Draft',
      description:
        'Your Submission has been saved. You can continue working on it later.',
      isClosable: true
    });

    navigate('/app/my-submissions');
  };

  const onSubmit = async (formPayload: unknown) => {
    const res = await mutation.mutateAsync({
      submissionContextId,
      articleWorkflowId,
      subtype: 'form',
      formPayload,
      articleTypeCategoryId
    });

    if (!res.isSuccess)
      return toast({
        variant: 'error',
        title: 'Error submitting updates',
        description:
          res.message?.errors
            ?.map((err: { reason: string }) => err.reason)
            .join(', ') || 'Try again later'
      });

    navigate('/app/my-submissions');

    return toast({
      variant: 'success',
      title: 'Missing Data Added',
      description: `Your Submission will be assigned to an Editor in ${journalName}.`,
      isClosable: true
    });
  };

  const hasCoAuthorConsentFields =
    configs.CoAuthorAgreeToSharing ||
    configs.CoAuthorsAgreeToEthics ||
    configs.CoAuthorManuscriptOriginalityConfirmed ||
    configs.CoAuthorManuscriptNotUnderConsiderationElsewhere ||
    configs.CoAuthorAuthorshipAndConflictDeclaration ||
    configs.CoAuthorResearchEthicsComplianceConfirmed ||
    configs.CoAuthorExportControlComplianceConfirmed;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
        <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
          <Layout width="95%" paddingY={2}>
            <Flex justifyContent="space-between" alignItems="center" mb={6}>
              <ButtonText
                variant="secondary"
                onClick={onClose}
                iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </ButtonText>
            </Flex>
            <Flex flexDirection={'column'} gap={4}>
              <Flex flexDirection="column">
                <Text textStyle="headingSection">
                  Review and Update Submission Details
                </Text>
                <Text>
                  Your submission was transferred. Please review the full
                  submission details and update the required information
                  {currentDeadline
                    ? ` by ${formatDeadlineDate(currentDeadline)}`
                    : ''}{' '}
                  before it can be assigned to editors.
                </Text>
              </Flex>
              <OutlineCard borderless padding={8} width="100%">
                <SubmissionInfoCard
                  showManuscript
                  headingText="Submission Transferred"
                  manuscriptLinkText="Manuscript"
                />
              </OutlineCard>

              {!hasMissingFields ? (
                <Banner
                  variant="success"
                  heading="Missing Fields Added"
                  content={`You have provided all the required data. Click the "Proceed" button below to continue.`}
                />
              ) : (
                <Flex flexDirection="column" gap={8}>
                  {/*--- Title Page --- */}

                  {configs.TitlePage && (
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4}>
                        <FormFieldSingleFileUpload
                          submissionContextId={submissionContext?.id!}
                          config={configs.TitlePage as FormFieldTitlePage}
                          methods={methods}
                          submissionId={submissionId}
                          width={{ base: '100%', md: '50%' }}
                          TitlePageSampleDownload={
                            (configs.TitlePageSampleDownload as FormFieldInfoText) ??
                            null
                          }
                        />
                      </Flex>
                    </OutlineCard>
                  )}

                  {/* --- Title --- */}
                  {configs.Title && (
                    <OutlineCard width="100%" padding={4}>
                      <Title
                        config={configs.Title as FormFieldTitle}
                        isMetadataExtracted={false}
                        editTitle={
                          reviewSubmissionInformation?.editTitle ??
                          'Edit Extracted Title'
                        }
                      />
                    </OutlineCard>
                  )}

                  {/* --- EnglishTitle --- */}
                  {configs.EnglishTitle && (
                    <CustomFormFieldComponent
                      config={configs.EnglishTitle as CustomFormFieldDefinition}
                    />
                  )}

                  {configs.Subtitle && (
                    <OutlineCard width="100%" padding={4}>
                      <GenericRichTextInput
                        config={configs.Subtitle as FormFieldSubtitle}
                      />
                    </OutlineCard>
                  )}

                  {configs.EnglishSubtitle && (
                    <CustomFormFieldComponent
                      config={
                        configs.EnglishSubtitle as CustomFormFieldDefinition
                      }
                    />
                  )}

                  {/* --- Abstract --- */}
                  {configs.Abstract && (
                    <OutlineCard width="100%" padding={4}>
                      <Abstract
                        config={configs.Abstract as FormFieldAbstract}
                        isMetadataExtracted={false}
                        editAbstract={
                          reviewSubmissionInformation?.editAbstract ??
                          'Edit Extracted Abstract'
                        }
                      />
                    </OutlineCard>
                  )}

                  {/* --- EnglishAbstract --- */}
                  {configs.EnglishAbstract && (
                    <CustomFormFieldComponent
                      config={
                        configs.EnglishAbstract as CustomFormFieldDefinition
                      }
                    />
                  )}

                  {/* --- Keywords --- */}
                  {configs.Keywords && (
                    <Keywords
                      config={configs.Keywords as FormFieldKeywords}
                      isMetadataExtracted={false}
                      editKeywords={
                        reviewSubmissionInformation?.editKeywords ??
                        'Edit Extracted Keywords'
                      }
                    />
                  )}

                  {configs.EnglishKeywords && (
                    <Box>
                      <CustomFormFieldComponent
                        config={
                          configs.EnglishKeywords as CustomFormFieldDefinition
                        }
                      />
                    </Box>
                  )}

                  {configs.Category && (
                    <OutlineCard width="100%" padding={4}>
                      <Flex flexDirection="column" gap={4} width="50%" mb={2}>
                        <CustomFormFieldComponent
                          config={configs.Category as CustomFormFieldDefinition}
                        />

                        {configs.CategoryOther && (
                          <CustomFormFieldComponent
                            config={
                              configs.CategoryOther as CustomFormFieldDefinition
                            }
                            placeholder="Please enter other category"
                          />
                        )}
                      </Flex>
                    </OutlineCard>
                  )}

                  {/* ----- Source File Upload ----- */}
                  {configs.SourceFiles && (
                    <SourceFileUpload
                      submissionId={submissionId}
                      config={
                        configs.SourceFiles as FormFieldFileUploadArray<'fileSet.SourceFiles'>
                      }
                      data-cy="sourcefiles-upload-component"
                    />
                  )}

                  {configs.StandardReportingGuidelines && (
                    <StandardReportingGuidelines
                      config={
                        configs.StandardReportingGuidelines as FormFieldStandardReportingGuidelines
                      }
                      CONSORTFilesConfig={
                        configs.CONSORTFiles as FormFieldCONSORTFiles
                      }
                      ARRIVEFilesConfig={
                        configs.ARRIVEFiles as FormFieldARRIVEFiles
                      }
                      submissionId={submissionId}
                      StandardReportingGuidelinesDetails={
                        configs.StandardReportingGuidelinesDetails as FormFieldStandardReportingGuidelinesDetails
                      }
                      StandardReportingGuidelinesFileDetails={
                        configs.StandardReportingGuidelinesFileDetails as FormFieldStandardReportingGuidelinesFileDetails
                      }

                      StandardReportingGuidelinesTextConfig={
                        configs.StandardReportingGuidelinesText as FormFieldTextInput<'preference.standardReportingGuidelinesText'>
                      }
                    />
                  )}

                  {/* ----- Supplementary Upload ----- */}
                  {configs.SupplementaryUpload && (
                    <SupplementaryUpload
                      submissionId={submissionId}
                      config={
                        configs.SupplementaryUpload as FormFieldSupplementary
                      }
                    />
                  )}

                  {configs.AdditionalSupplementary && (
                    <AdditionalSupplementaryUpload
                      submissionId={submissionId}
                      config={
                        configs.AdditionalSupplementary as FormFieldAdditionalSupplementary
                      }
                      data-cy="additional-supplementary-upload-component"
                    />
                  )}

                  {configs.CoverLetter && (
                    <CoverLetter
                      submissionId={submissionId}
                      config={configs.CoverLetter as FormFieldCoverLetter}
                    />
                  )}

                  {configs.Figures && (
                    <Figures
                      config={configs.Figures as FormFieldFiguresInput}
                      submissionId={submissionId}
                    />
                  )}
                  {configs.GraphicalAbstractImage && (
                    <GraphicalAbstractImageUpload
                      config={
                        configs.GraphicalAbstractImage as FormFieldGraphicalAbstractImage
                      }
                      submissionId={submissionId}
                    />
                  )}
                  {configs.Tables && (
                    <Tables
                      config={configs.Tables as FormFieldTablesInput}
                      submissionId={submissionId}
                    />
                  )}

                  {configs.NumberOfFigures && (
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4} width="50%" mb={2}>
                        <FormFieldInteger
                          config={
                            configs.NumberOfFigures as FormFieldIntegerInput
                          }
                        />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.NumberOfColorFigures && (
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4} width="50%" mb={2}>
                        <FormFieldInteger
                          config={
                            configs.NumberOfColorFigures as FormFieldIntegerInput
                          }
                        />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.NumberOfTables && (
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4} width="50%" mb={2}>
                        <FormFieldInteger
                          config={
                            configs.NumberOfTables as FormFieldIntegerInput
                          }
                        />
                      </Flex>
                    </OutlineCard>
                  )}
                  {configs.NumberOfWords && (
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4} width="50%" mb={2}>
                        <FormFieldInteger
                          config={
                            configs.NumberOfWords as FormFieldIntegerInput
                          }
                        />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.GeneralClassifications && (
                    <GeneralClassifications
                      config={configs.GeneralClassifications as any}
                    />
                  )}

                  {/* ----- Author list ----- */}
                  {(configs.ContactAuthor ||
                    configs.CorrespondingAuthor ||
                    configs.CoAuthors ||
                    hasCoAuthorConsentFields) && (
                    <Flex flexDirection="column" gap={8}>
                      <Box>
                        <Text textStyle="headingGroup">Authors</Text>
                        <Text>Provide information about your authors.</Text>
                      </Box>

                      {hasCoAuthorConsentFields && (
                        <CoAuthorConsent
                          configs={configs as IAuthorStepConfig}
                        />
                      )}

                      {configs.ContactAuthor && (
                        <Author
                          config={configs.ContactAuthor}
                          authorList={authorList}
                          value={JSON.stringify(
                            watch(
                              configs.ContactAuthor.caseDataFields.main as any
                            )
                          )}
                        />
                      )}

                      {configs.CorrespondingAuthor && (
                        <CorrespondingAuthor
                          config={
                            configs.CorrespondingAuthor as FormFieldCorrespondingAuthor
                          }
                        />
                      )}
                    </Flex>
                  )}


                  {configs.CommentForEditor && (
                    <GenericRichTextInput config={configs.CommentForEditor as FormFieldCommentForEditor} />
                  )}
                  
                  {/* ----- Data Availability ----- */}
                  {configs.DataAvailability &&
                    (configs.DataAvailabilityOpenlyAvailableUrl ||
                      (configs.DataAvailabilityEmbargoUrl &&
                        configs.DataAvailabilityEmbargoDate) ||
                      configs.DataUnavailabilityReason) && (
                      <DataAvailability
                        config={
                          configs.DataAvailability as FormFieldDataAvailability
                        }
                        DataAvailabilityOpenlyAvailableUrlConfig={
                          configs.DataAvailabilityOpenlyAvailableUrl as FormFieldTextInput<'preference.dataAvailabilityOpenlyAvailableUrl'>
                        }
                        DataAvailabilityEmbargoUrlConfig={
                          configs.DataAvailabilityEmbargoUrl as FormFieldTextInput<'preference.dataAvailabilityEmbargoUrl'>
                        }
                        DataAvailabilityEmbargoDateConfig={
                          configs.DataAvailabilityEmbargoDate as FormFieldDateInput<'preference.dataAvailabilityEmbargoDate'>
                        }
                        DataUnavailabilityReasonConfig={
                          configs.DataUnavailabilityReason as FormFieldRadio<'preference.dataUnavailabilityReason'>
                        }
                      />
                    )}

                  {configs.DataAvailabilityStatement && (
                    <DataAvailabilityStatement
                      config={
                        configs.DataAvailabilityStatement as FormFieldCheckboxArray<'preference.dataAvailabilityStatement'>
                      }
                       DataAvailabilityTextConfig={configs.DataAvailabilityText as FormFieldTextInput<'preference.dataAvailabilityText'> ?? null}
                    />
                  )}

                  {configs.NoveltyStatement && (
                    <Flex flexDirection="column">
                      <Box>
                        <Text textStyle="headingGroup">
                          {submissionPreferences?.motivationToPublish.mainLabel}
                        </Text>
                        <Text>
                          {
                            submissionPreferences?.motivationToPublish
                              .secondaryLabel
                          }
                        </Text>
                      </Box>

                      {configs.NoveltyStatement && (
                        <NoveltyStatement
                          config={
                            configs.NoveltyStatement as FormFieldNoveltyStatement
                          }
                        />
                      )}
                    </Flex>
                  )}

                  {/* Figures and Tables */}
                  {configs.FiguresAndTables && (
                    <FiguresAndTables
                      fieldConfig={
                        configs.FiguresAndTables as FormFieldFiguresAndTables
                      }
                      submissionContextId={submissionContextId}
                    />
                  )}

                  {(configs.LicensesAndPermissions ||
                    configs.LicensesAndPermissionsReproducedContent ||
                    configs.LicensesAndPermissionsFilesConfig) && (
                    <Flex flexDirection="column" gap={4}>
                      <LicensesAndPermissionForm
                        draftSubmissionId={submissionId}
                        LicensesAndPermissionsConfig={
                          configs.LicensesAndPermissions as FormFieldRadio<'metadata.LicensesAndPermissions'>
                        }
                        LicensesAndPermissionsReproducedContentConfig={
                          configs.LicensesAndPermissionsReproducedContent as FormFieldTextInput<'metadata.LicensesAndPermissionsReproducedContent'>
                        }
                        LicensesAndPermissionsFilesConfig={
                          configs.LicensesAndPermissionsFiles as FormFieldFileUploadArray<'fileSet.LicensesAndPermissionsFiles'>
                        }
                      />
                    </Flex>
                  )}

                  {/* ----- Peer Review ----- */}
                  {(configs.TransparentPeerReview ||
                    configs.DoubleAnonymousPeerReview ||
                    configs.RecommendedReviewers ||
                    configs.FocusIssue ||
                    configs.OpposedReviewers) && (
                    <Flex flexDirection="column" gap={8}>
                      <Box>
                        <Text textStyle="headingGroup">
                          Submission Preferences
                        </Text>
                        <Text>
                          Select the following preferences to tailor how your
                          manuscript is processed and published.
                        </Text>
                      </Box>

                      {configs.FocusIssue && (
                        <FocusIssue
                          config={configs.FocusIssue as FormFieldFocusSelect}
                          guidelineLink={guidelineLink}
                        />
                      )}

                      {/* ----- Double Anonymous Peer Review ----- */}
                      {configs.DoubleAnonymousPeerReview &&
                        configs.AnonymizedManuscript && (
                          <DoubleAnonymousReview
                            submissionId={submissionId}
                            existsConfig={
                              configs.DoubleAnonymousPeerReview as FormFieldDoubleAnonymousPeerReview
                            }
                            valueConfig={
                              configs.AnonymizedManuscript as FormFieldAnonymizedManuscript
                            }
                          />
                        )}

                      {/* Recommended Reviewers */}
                      {configs.RecommendedReviewers && (
                        <ReviewerRecommendation
                          draftId={submissionId}
                          ctxId={submissionContextId}
                          lexiconProjectId={
                            submissionContext?.config.lexiconProjectId ?? ''
                          }
                          config={
                            configs.RecommendedReviewers as FormFieldRecommendedReviewers
                          }
                          type="submission"
                        />
                      )}

                      {/* Opposed Reviewers */}
                      {configs.OpposedReviewers && (
                        <ReviewerRecommendation
                          draftId={submissionId}
                          ctxId={submissionContextId}
                          lexiconProjectId={
                            submissionContext?.config.lexiconProjectId ?? ''
                          }
                          config={
                            configs.OpposedReviewers as FormFieldOpposedReviewers
                          }
                          type="submission"
                        />
                      )}

                      {/* ----- Transparent Peer Review ----- */}
                      {configs.TransparentPeerReview && (
                        <TransparentPeerReview
                          config={
                            configs.TransparentPeerReview as FormFieldTransparentPeerReview
                          }
                        />
                      )}
                    </Flex>
                  )}

                  {/* ----- Ethics ----- */}
                  {configs.EthicsStatement && (
                    <EthicsStatement
                      config={
                        configs.EthicsStatement as FormFieldEthicsStatement
                      }
                    />
                  )}

                  {/* ----- Clinical Trials ----- */}
                  {configs.ClinicalTrials && (
                    <ClinicalTrials
                      config={configs.ClinicalTrials as FormFieldClinicalTrials}
                    />
                  )}

                  {/* ----- Funding ----- */}
                  {configs.Funding && (
                    <Funding config={configs.Funding as FormFieldFunding} />
                  )}

                  {/* ----- Funding Generic ----- */}
                  {configs.FundingGeneric && (
                    <FundingGeneric
                      config={configs.FundingGeneric as FormFieldFundingGeneric}
                    />
                  )}

                  {/* ----- Publishing Model ----- */}
                  {configs.PublishingModel && (
                    <PublishingModel
                      config={
                        configs.PublishingModel as FormFieldPublishingModel
                      }
                    />
                  )}

                  {configs.OpenAccessChoice && (
                    <RadioGroup
                      config={
                        configs.OpenAccessChoice as FormFieldRadio<'preference.openAccessChoice'>
                      }
                    />
                  )}

                  <TextInput
                    config={
                      configs.OpenAccessChoiceWorldBankACountry as FormFieldTextInput<'preference.openAccessChoiceWorldBankACountry'>
                    }
                    placeholder="Enter your country or region"
                  />

                  <TextInput
                    config={
                      configs.OpenAccessChoiceWorldBankBCountry as FormFieldTextInput<'preference.openAccessChoiceWorldBankBCountry'>
                    }
                    placeholder="Enter your country or region"
                  />

                  <TextInput
                    config={
                      configs.OpenAccessChoiceReviewerDiscountRate as FormFieldTextInput<'preference.openAccessChoiceReviewerDiscountRate'>
                    }
                    placeholder="Enter an ID"
                  />

                  {/* ----- Immediate Publication Consent ----- */}
                  {configs.PreferenceImmediatePublication && (
                    <ImmediatePublication
                      config={
                        configs.PreferenceImmediatePublication as FormFieldPreferenceImmediatePublication
                      }
                    />
                  )}

                  {/* ----- Additional Preferences ----- */}
                  {(configs.AcceleratedPublication ||
                    configs.IPEMMembership ||
                    configs.SubjectArea) && (
                    <Flex flexDirection="column" gap={8} width="100%">
                      <Text textStyle="headingGroup">
                        Additional Preferences
                      </Text>

                      {configs.AcceleratedPublication && (
                        <AcceleratedPublication
                          config={
                            configs.AcceleratedPublication as FormFieldAcceleratedPublication
                          }
                        />
                      )}

                      {configs.IPEMMembership && (
                        <TextInput
                          config={
                            configs.IPEMMembership as FormFieldIPEMMembership
                          }
                          placeholder="eg., SOMEID9999"
                        />
                      )}

                      {configs.SubjectArea && (
                        <SubjectArea
                          config={
                            configs.SubjectArea as FormFieldSelect<'preference.subjectArea'>
                          }
                        />
                      )}
                    </Flex>
                  )}

                  {configs.PreviouslySubmitted && (
                    <RadioGroupWithTextArea
                      config={
                        configs.PreviouslySubmitted as FormFieldPreviouslySubmitted
                      }
                      placeholder="Enter manuscript ID"
                      setValue={setValue as any}
                    />
                  )}

                  {configs.ConflictOfInterestForAuthor && (
                    <RadioGroupWithTextArea
                      config={
                        configs.ConflictOfInterestForAuthor as FormFieldConflictOfInterestForAuthor
                      }
                      placeholder="Enter conflict of interest"
                      setValue={setValue as any}
                    />
                  )}

                  {configs.AITools && (
                    <AITools config={configs.AITools as FormFieldAITools} />
                  )}

                  {configs.Preprint && (
                    <RadioGroupWithTextArea
                      config={configs.Preprint as FormFieldPreprint}
                      placeholder="Enter preprint link"
                      setValue={setValue as any}
                    />
                  )}

                  {configs.SharedDataset && (
                    <RadioGroupWithTextArea
                      config={configs.SharedDataset as FormFieldSharedDataset}
                      placeholder="Enter relevant extant publications"
                      setValue={setValue as any}
                    />
                  )}

                  {(configs.GeneratedDataset || configs.UtilizedDataset) && (
                    <OutlineCard padding={4} width="100%">
                      <Flex
                        flexDirection="column"
                        gap={4}
                        id="Datasets Statement"
                      >
                        <FieldLabel
                          label="Datasets Statement"
                          subText="In the event of acceptance, this text/information will be published: enter it accurately, carefully and completely."
                          isRequired
                        />
                        {configs.GeneratedDataset && (
                          <GeneratedDataset
                            config={
                              configs.GeneratedDataset as FormFieldGeneratedDataset
                            }
                            hideLabel
                          />
                        )}
                        {configs.UtilizedDataset && (
                          <UtilizedDataset
                            config={
                              configs.UtilizedDataset as FormFieldUtilizedDataset
                            }
                            hideLabel
                          />
                        )}
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.EthicalApproval && (
                    <RadioGroupWithTextArea
                      config={
                        configs.EthicalApproval as FormFieldEthicalApproval
                      }
                      placeholder="Enter ethical approval"
                      setValue={setValue as any}
                    />
                  )}
                  {configs.EthicsApproval && (
                    <RadioGroupWithTextArea
                      config={
                        configs.EthicsApproval as FormFieldEthicalApproval
                      }
                      placeholder="Enter ethics approval"
                      setValue={setValue as any}
                    />
                  )}

                  

                  <Flex flexDirection="column" gap={8} width="100%">
                    <OutlineCard padding={4}>
                      <Flex flexDirection="column" gap={4}>
                        {(configs.ManuscriptOwnership ||
                          configs.BlindedReviewConsent ||
                          configs.ApcAgreement ||
                          configs.OpenAccessPublishingConsent ||
                          configs.EthicsChecklist ||
                          configs.OverlappingMaterial ||
                          configs.BlindedPeerReviewConsent ||
                          configs.OpenAccessOnly ||
                          configs.AuthorDeclaration) && (
                          <FieldLabel
                            label="Confirmations"
                            subText=""
                            isRequired
                          />
                        )}

                        {configs.ManuscriptOwnership && (
                          <CustomFormFieldComponent
                            config={
                              configs.ManuscriptOwnership as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.BlindedReviewConsent && (
                          <CustomFormFieldComponent
                            config={
                              configs.BlindedReviewConsent as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.ApcAgreement && (
                          <CustomFormFieldComponent
                            config={
                              configs.ApcAgreement as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {/* --- Open Access Only ---- */}
                        {configs.OpenAccessOnly && (
                          <OpenAccessOnly
                            config={configs.OpenAccessOnly as FormFieldCheckbox}
                          />
                        )}

                        {configs.OpenAccessPublishingConsent && (
                          <CustomFormFieldComponent
                            config={
                              configs.OpenAccessPublishingConsent as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.EthicsChecklist && (
                          <CustomFormFieldComponent
                            config={
                              configs.EthicsChecklist as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.OverlappingMaterial && (
                          <CustomFormFieldComponent
                            config={
                              configs.OverlappingMaterial as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.BlindedPeerReviewConsent && (
                          <CustomFormFieldComponent
                            config={
                              configs.BlindedPeerReviewConsent as CustomFormFieldDefinition
                            }
                          />
                        )}

                        {configs.AuthorDeclaration && (
                          <AuthorDeclaration
                            config={
                              configs.AuthorDeclaration as FormFieldCheckbox
                            }
                            journalName={journalName}
                          />
                        )}
                      </Flex>
                    </OutlineCard>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Layout>
          <Flex
            padding={4}
            bottom={0}
            zIndex={99}
            justifyContent="center"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Flex width="100%" justifyContent="space-between">
              <Button variant="caution" onClick={onClose}>
                Exit
              </Button>
              <Flex gap={2}>
                <Button
                  isDisabled={mutation.isLoading}
                  onClick={onSaveDraft}
                  variant="secondary"
                >
                  Save Draft
                </Button>
                <Button type="submit" isLoading={mutation.isLoading}>
                  {hasMissingFields ? 'Submit' : 'Proceed'}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
